import { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import checkMark from 'assets/checkMark.svg';
import digitalServicesIcon from 'assets/digitalService.svg';
import pageTemplatesIcon from 'assets/pageTemplates.svg';
import rightSmall from 'assets/right-small.svg';
import serviceChecklistIcon from 'assets/serviceChecklist.svg';
import spacesServices from 'assets/spacesServices.svg';

import ReviewNoFeedback from 'Components/ReviewNoFeedback';

import { categorizeFeedback, getCategoryColor } from 'utlis/categorizeFeedback';
import { COMMON_FEEDBACK_CATEGORIES, DESIGN_REVIEW_STATUS, TEMPLATE_TITLES, VALUE_MAPPINGS } from 'utlis/constants';

import OverviewReport from './OverviewReport';
import ScoreCircle from './ScoreCircle';

import styles from './styles.module.scss';

const CATEGORIES = {
  TAMM_SPACES_SERVICES: 'TAMM Spaces Services Onboarding Guidelines',
  DIGITAL_SERVICE_USABILITY: 'Digital Service Usability & Accessibility Standards',
  ADGES_SERVICE_CHECKLIST: 'ADGEs Service Checklist',
  TAMM_PAGE_TEMPLATES: 'TAMM Page Templates',
};

const ReviewCompleted = ({ title, reviewData, onCategoryClick }) => {
  const summary = useMemo(() => buildFinalFeedbackSummary(title, reviewData), [reviewData]);

  if (summary) {
    return renderFinalSummaryView({ summary, onCategoryClick, reviewData });
  }

  const processedAuditData = useMemo(() => processDesignAuditReview(reviewData), [reviewData]);

  // Review completed but no feedback (final_feedback is undefined, null, or empty array)
  const hasNoFeedback = reviewData?.final_feedback == null
    || (Array.isArray(reviewData.final_feedback) && reviewData.final_feedback.length === 0);
  const isCompletedWithNoFeedback = Boolean(
    reviewData
    && reviewData.review_status === DESIGN_REVIEW_STATUS.DONE
    && hasNoFeedback
  );

  if (isCompletedWithNoFeedback) {
    return <ReviewNoFeedback />;
  }

  if (!processedAuditData) {
    return (
      <div className="flex flex-col gap-24 w-full flex-1">
        <div className="text-center text-gray-500">
          Loading review data...
        </div>
      </div>
    );
  }

  return renderAuditSummary(processedAuditData);
};

function renderGroupButton(group, onCategoryClick) {
  return (
    <button
      key={group.categoryKey || group.label}
      className={cn(styles['feedback-group'], 'cursor-pointer')}
      onClick={() => onCategoryClick && onCategoryClick(group.categoryKey || group.label)}
    >
      <div className={styles['feedback-group-label']}>
        <img className="inline-block" src={group.icon || getCategoryData(group.label)?.icon} alt="" />
        {VALUE_MAPPINGS[group.label] || group.label}
      </div>
      <div className={styles['feedback-group-right']}>
        <span
          className={styles['feedback-group-count']}
          style={{ color: group.severityColor }}
        >
          {group.count}
        </span>
        <span className={styles['feedback-group-arrow']}>
          <img src={rightSmall} alt="right small" />
        </span>
      </div>
    </button>
  );
}

function renderFinalSummaryView({ summary, onCategoryClick, reviewData }) {
  const overview = reviewData?.extra_data?.post_processes?.overview?.data;

  const templateDisplayName = summary.template === TEMPLATE_TITLES.UX_CRITIQUE ? 'Growth Audit' : summary.template;

  const newIdeasCategoryName = COMMON_FEEDBACK_CATEGORIES.DEEPER_INSIGHTS?.categoryName;
  const newIdeasGroup = summary.groups.find(g => g.label === newIdeasCategoryName);
  const otherGroups = summary.groups.filter(g => g.label !== newIdeasCategoryName);

  return (
    <div className="flex flex-col gap-16 w-full flex-1">
      <div className="flex items-center gap-16 mt-32">
        {summary.score != null && <ScoreCircle score={summary.score} size={64} />}
        <div className="flex flex-col items-start gap-4">
          <h3 className={styles['review-title']} title={summary.title}>
            {summary.title}
          </h3>
          <div className={styles['review-meta']}>
            <span className={styles['review-meta-item']}>
              {summary.findingsLabel}
            </span>
            {templateDisplayName && (
              <>
                <span className={styles['review-meta-item']}>
                  |
                </span>
                <span className={styles['review-meta-item']}>
                  {templateDisplayName}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {overview && <OverviewReport overview={overview} />}

      <div className={styles['findings-section']}>
        <h3 className={styles['findings-heading']}>
          Findings
        </h3>

        {newIdeasGroup && (
          <div className={styles['new-ideas-wrapper']}>
            {renderGroupButton(newIdeasGroup, onCategoryClick)}
          </div>
        )}

        {otherGroups.length > 0 && (
          <div className={styles['behavioral-section']}>
            <p className={styles['behavioral-insights-label']}>
              Behavioral insights
            </p>
            <div className={styles['categories-grid']}>
              {otherGroups.map(group => renderGroupButton(group, onCategoryClick))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function renderAuditSummary(data) {
  return (
    <div className="flex flex-col gap-24 w-full flex-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-16">
          <div className="flex flex-col gap-8">
            <h3 className="text-md 2xl:text-lg font-medium text-gray-900">
              {VALUE_MAPPINGS[data.title] || data.title}
            </h3>
            <p className="flex sm:text-xs 2xl:text-sm text-gray-500">
              {data.totalFindings}
              {' '}
              finding
              {data.totalFindings === 1 ? '' : 's'}
              <div className="mx-8 w-[1px] bg-gray-200" />
              {data.reviewType}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-12">
        {data.categories.map(category => (
          <div
            key={category.id}
            className="flex items-center justify-between p-3 2xl:p-4 border border-gray-200 rounded-lg
            hover:bg-gray-50 transition-colors"
            style={{ boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.04)' }}
          >
            <div className="flex items-center gap-16">
              <span className="text-md 2xl:text-lg">
                <img src={category.icon} alt={category.title} />
              </span>
              <span
                className={cn(
                  'sm:text-xs 2xl:text-sm font-medium',
                  {
                    'text-primary-500': category.findings === 0,
                    'text-gray-900': category.findings !== 0,
                  }
                )}
              >
                {category.title}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {category.findings === 0
                ? (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full">
                    <span className="text-green-600 text-md">
                      <img src={checkMark} alt="" />
                    </span>
                  </div>
                )
                : (
                  <div className={cn('flex items-center gap-4', getStatusColor(category.findings))}>
                    <span className="text-xs">
                      {category.findings}
                    </span>
                    <span className="text-xs">
                      findings
                    </span>
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildFinalFeedbackSummary(title, reviewData) {
  if (!Array.isArray(reviewData?.final_feedback) || reviewData.final_feedback.length === 0) {
    return null;
  }

  // Use categorizeFeedback function to properly categorize feedback including Deeper Insights
  const reviewType = reviewData?.extra_data?.template ?? reviewData?.template;
  const categorizedFeedback = categorizeFeedback(reviewData.final_feedback || [], reviewType, reviewData);

  // Build groups from categorized feedback instead of raw final_feedback
  const groups = categorizedFeedback.map(category => ({
    label: category.categoryName,
    categoryKey: category.categoryKey, // Add categoryKey for onCategoryClick
    count: category.categoryFeedback.length,
    countLabel: `${category.categoryFeedback.length} finding${category.categoryFeedback.length === 1 ? '' : 's'}`,
    icon: category.categoryIcon,
    severityColor: getCategoryColor(category.categoryFeedback), // Use new color logic
    categoryFeedback: category.categoryFeedback, // Pass feedback for color calculation
  }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  // Calculate total findings from categorized feedback (including Deeper Insights)
  const totalFindings = categorizedFeedback.reduce((total, category) => total + category.categoryFeedback.length, 0);

  const rawScore = reviewData?.extra_data?.post_processes?.overview?.data?.score_assessment?.score;

  // Normalize score to a display
  let scoreDisplay = null;

  if (rawScore != null) {
    if (typeof rawScore === 'number') {
      scoreDisplay = String(rawScore);
    }
    else if (typeof rawScore === 'string' && rawScore.trim() !== '') {
      scoreDisplay = rawScore.trim().replace(/^\+/, '');
    }
  }
  const scoreNumeric = scoreDisplay != null ? Number(scoreDisplay) : NaN;
  const score = isFinite(scoreNumeric) ? scoreDisplay : null;

  return {
    title: title || reviewData?.extra_data?.review_frame_name || 'New design review',
    findingsLabel: `${totalFindings} finding${totalFindings === 1 ? '' : 's'}`,
    template: reviewType ?? null,
    score,
    groups,
  };
}

function processDesignAuditReview(data) {
  if (!data || !data.feedbacks) {
    return null;
  }

  const groupedFeedbacks = data.feedbacks.reduce((acc, feedback) => {
    const source = feedback.design_quality_source;

    if (!acc[source]) {
      acc[source] = 0;
    }
    acc[source] += 1;

    return acc;
  }, {});

  const categoryIcons = {
    [CATEGORIES.TAMM_SPACES_SERVICES]: spacesServices,
    [CATEGORIES.DIGITAL_SERVICE_USABILITY]: digitalServicesIcon,
    [CATEGORIES.ADGES_SERVICE_CHECKLIST]: serviceChecklistIcon,
    [CATEGORIES.TAMM_PAGE_TEMPLATES]: pageTemplatesIcon,
  };

  const allCategories = [
    CATEGORIES.TAMM_SPACES_SERVICES,
    CATEGORIES.DIGITAL_SERVICE_USABILITY,
    CATEGORIES.ADGES_SERVICE_CHECKLIST,
    CATEGORIES.TAMM_PAGE_TEMPLATES,
  ];

  const categories = allCategories.map((categoryName, index) => ({
    id: index + 1,
    icon: categoryIcons[categoryName] || digitalServicesIcon,
    title: categoryName,
    findings: groupedFeedbacks[categoryName] || 0,
  }));

  let keyCategories = [
    CATEGORIES.DIGITAL_SERVICE_USABILITY,
    CATEGORIES.TAMM_SPACES_SERVICES,
  ];

  if (
    data?.audit_data
    && Object.prototype.hasOwnProperty.call(data.audit_data, 'has_tamm_guideline')
    && data.audit_data.has_tamm_guideline === false
  ) {
    keyCategories = keyCategories.filter(cat => cat !== CATEGORIES.TAMM_SPACES_SERVICES);
  }

  const categoriesToShow = categories.filter(
    cat => groupedFeedbacks[cat.title] > 0 || keyCategories.includes(cat.title)
  );

  let reviewType = 'Design Review';

  if (data?.customer?.name) {
    reviewType = `${data.customer.name} Review`;
  }

  return {
    title: data.title,
    totalFindings: data.feedbacks.length,
    reviewType,
    categories: categoriesToShow,
  };
}

function getStatusColor(findings) {
  if (findings === 0) {
    return 'text-primary-500';
  }
  if (findings < 5) {
    return 'text-yellow-600';
  }

  return 'text-red-500';
}

function getCategoryData(categoryName) {
  const categoryKey = categoryName.toUpperCase().replace(/\s+/g, '_');

  return COMMON_FEEDBACK_CATEGORIES[categoryKey];
}

ReviewCompleted.propTypes = {
  title: PropTypes.string.isRequired,
  reviewData: PropTypes.object,
  onCategoryClick: PropTypes.func,
};

export default ReviewCompleted;
